import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import Map from 'ol/Map';
import { View } from 'ol';
import Overlay from 'ol/Overlay';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import * as layer from 'ol/layer';
import { Tile as Tile, Vector as VectorLayer, Image as ImageLayer } from 'ol/layer';
import { OSM as OSM, Vector as VectorSource, Vector as MeasureSource } from 'ol/source';
import { WFS, GeoJSON } from 'ol/format';
import ImageWMS from 'ol/source/ImageWMS';
import { Icon, Style, Fill, Stroke } from 'ol/style';
import * as control from 'ol/control';
import * as proj from 'ol/proj';
import KML from 'ol/format/KML';
import { v4 as uuidv4 } from 'uuid';   

@Component({
  selector: 'app-map-preview',
  templateUrl: './map-preview.component.html',
  styleUrls: ['./map-preview.component.css']
})
export class MapPreviewComponent implements OnChanges, OnDestroy {

  @Input() kmlFile: File | null = null;
  private map: Map | null = null;
  private popup: Overlay | null = null;

   /** give each instance its own div id */
  mapDivId = `kml-preview-${uuidv4()}`;

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kmlFile']) {
   
      const newFile = changes['kmlFile'].currentValue;
        // console.log(newFile)
      if (newFile) {
        this.loadKmlPreview(newFile);
      } else {
        this.clearMap();
      }
    }
  }

  ngOnDestroy(): void {
    this.clearMap();
  }

  private loadKmlPreview(file: File): void {
  const imegary = new Tile({
      visible: true,
      source: new OSM({
        url: 'https://mt{0-3}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
         attributions: '' 
      }),
     
    });
   const streetmap = new Tile({
      visible: false,
      source: new OSM({
        url: 'https://mt{0-3}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
         attributions: '' 
      }),
       
    });
  

    // State Boundary
   const StateBoundary = new layer.Image({
      source: new ImageWMS({
        url: 'https://mapserver.odisha4kgeo.in/geoserver/iwlms/wms',
        params: {
          'FORMAT': 'image/png',
          'VERSION': '1.1.0',
          STYLES: '',
          LAYERS: 'iwlms:odisha_state_boundary'
        },
        serverType: 'geoserver',
        crossOrigin: 'anonymous'
      }),
      visible: true
    }); 

    const reader = new FileReader();
    reader.onload = (e) => {
      const kmlText = e.target?.result as string;
      if (!kmlText) {
        this.clearMap();
        return;
      }

      try {
        const kmlFormat = new KML();
        const features = kmlFormat.readFeatures(kmlText, {
          featureProjection: 'EPSG:3857', // Project to Web Mercator, used by OpenLayers
          extractStyles: false // Ignore styles defined in the KML file 
        });

        if (features.length === 0) {
          console.warn('No features found in KML file');
          this.clearMap();
          return;
        }
        // Clear any existing styles on features to ensure layer style applies
        features.forEach(feature => {
          feature.setStyle(null);
          //console.log('Feature style cleared:', feature.getId());
        });

        // Create a vector source and layer for the KML features
        const vectorSource = new VectorSource({
          features: features
        });
    
        const _style=new Style({
              stroke: new Stroke({
                color: 'rgba(244, 15, 7, 0.9)' ,
                width: 2
              }),
              fill: new Fill({
                  //color: 'rgba(0, 0, 0, 0)'  // Transparent fill
                  color: 'rgba(0, 0, 255, 0.1)' // Semi-transparent blue fill (changed for visibility)
                })
            });
        const vectorLayer = new VectorLayer({
          source: vectorSource,
          style: _style
        });

        // Initialize or update the map
        this.clearMap(); // Clean up any existing map

        this.map = new Map({
          target: this.mapDivId,
          layers: [
            // new Tile({
            //   source: new OSM()
            // }),
            imegary,streetmap,StateBoundary,
            vectorLayer
          ],
          view: new View({
             center: proj.fromLonLat([85.190055, 20.886785]),
             zoom: 6,
             maxZoom: 23,
             minZoom: 5,
          }),
         // controls: [], // Remove default controls for a cleaner preview
          //interactions: [] // Disable interactions for preview
        });
       
        // Fit the map to the extent of the KML features
        const extent = vectorSource.getExtent();
        if (!isFinite(extent[0]) || !isFinite(extent[1]) || !isFinite(extent[2]) || !isFinite(extent[3])) {
          console.warn('Invalid KML extent');
          this.clearMap();
          return;
        }

        this.map.getView().fit(extent, {
          padding: [10, 10, 10, 10],
          maxZoom: 15,
          duration: 0
        });

         // Initialize popup overlay
      const popupElement = document.getElementById('popup');
      if (popupElement) {

         this.popup = new Overlay({
        element: popupElement,
        autoPan: false, // Disable autoPan to allow dragging
        autoPanAnimation: {
          duration: 250
        }
      });
        this.map.addOverlay(this.popup);
      }


       // Pointermove event to change cursor on hover
    this.map.on('pointermove', (event) => {
      const pixel = event.pixel;
      const feature = this.map!.forEachFeatureAtPixel(pixel, (feat) => feat, {
        hitTolerance: 10 // Match the click tolerance
      });

      // Change cursor to pointer if a feature is found, otherwise revert to default
      const mapElement = this.map!.getTargetElement();
      mapElement.style.cursor = feature ? 'pointer' : 'auto';
    });
     this.map.on('click', (event) => {
      const pixel = event.pixel;
      const feature = this.map!.forEachFeatureAtPixel(pixel, (feat) => feat, {
        hitTolerance: 10
      });
      if (feature) {
        const coordinates = (feature.getGeometry() as Point).getCoordinates();
        const properties = feature.getProperties();
        this.showPopup(coordinates, properties);
      } else {
        this.popup?.setPosition(undefined);
      }
      });
      } catch (error) {
        console.error('Error parsing KML file:', error);
        this.clearMap();
      }
    };
    reader.readAsText(file);
  }
    private showPopup(coordinates: number[], properties: any): void {
    if (!this.popup) return;

    const content = document.getElementById('popup-content');
    if (content) {
      // Build the popup HTML with a table structure
      let popupHtml = `
        <div class="popup-header">
          <span class="popup-title">Feature Properties</span>
          <button class="popup-close">×</button>
        </div>
        <div class="popup-content-body">
          <table class="table table-hover table-bordered popup-table">
            <thead class="table-header">
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
      `;

      for (const [key, value] of Object.entries(properties)) {
        // Skip geometry, OpenLayers internal properties, id, and styleUrl
        if (key === 'geometry' || key.startsWith('ol_') || key.toLowerCase() === 'id' || key.toLowerCase() === 'styleurl') continue;

        const displayValue = typeof value === 'object' && value !== null
          ? JSON.stringify(value)
          : (value ?? 'N/A');

        popupHtml += `
          <tr>
            <td><strong>${key.charAt(0).toUpperCase() + key.slice(1)}</strong></td>
            <td>${displayValue}</td>
          </tr>
        `;
      }

      popupHtml += `
            </tbody>
          </table>
        </div>
      `;

      content.innerHTML = popupHtml;

      const popupElement = document.getElementById('popup');
      if (popupElement) {
        popupElement.style.display = 'block';

        // Make the popup draggable
        const header = popupElement.querySelector('.popup-header') as HTMLElement;
        let isDragging = false;
        let currentX: number;
        let currentY: number;
        let initialX: number;
        let initialY: number;

        const onMouseDown = (e: MouseEvent) => {
          isDragging = true;
          initialX = e.clientX - currentX;
          initialY = e.clientY - currentY;
          document.addEventListener('mousemove', onMouseMove);
          document.addEventListener('mouseup', onMouseUp);
        };

        const onMouseMove = (e: MouseEvent) => {
          if (isDragging) {
            currentX = e.clientX - initialX;
            currentY = e.clientY - initialY;
            popupElement.style.left = `${currentX}px`;
            popupElement.style.top = `${currentY}px`;
            // Update the overlay position to match the new pixel position
            const pixelPosition = [e.clientX, e.clientY];
            const mapPosition = this.map!.getCoordinateFromPixel(pixelPosition);
            this.popup!.setPosition(mapPosition);
          }
        };

        const onMouseUp = () => {
          isDragging = false;
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', onMouseUp);
        };

        if (header) {
          // Initialize position offsets
          const rect = popupElement.getBoundingClientRect();
          currentX = rect.left;
          currentY = rect.top;

          header.style.cursor = 'move';
          header.addEventListener('mousedown', onMouseDown);
        }

        const closeButton = content.querySelector('.popup-close');
        if (closeButton) {
          closeButton.addEventListener('click', () => {
            popupElement.style.display = 'none';
            this.popup?.setPosition(undefined);
          });
        }
      }
    }

    this.popup.setPosition(coordinates);
  }

  private clearMap(): void {
    if (this.map) {
      this.map.setTarget(undefined);
      this.map = null;
    }
  }
}